import {useMemo} from "react";
import DOMPurify from "dompurify";
import {marked} from "marked";
import ArrowUpIcon from "@atlaskit/icon/core/arrow-up";
import ToolsIcon from "@atlaskit/icon/core/tools";
import ChevronUpIcon from "@atlaskit/icon/core/chevron-up";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import {AnankeTooltip} from "@vesta/ananke/components/anankeTooltip";
import LightbulbIcon from "@atlaskit/icon/core/lightbulb";
import {AnankeTempLozenge} from "~/components/anankeTemp/anankeTempLozenge";
import {
    AMARON__EVENT_CONTAINER__SALES_ASSISTANCE_DATA__COLUMN_ID,
    SalesAssistanceSchema,
    salesAssistanceDataValidator,
    amaronServiceRequestedTypes,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import {
    AmaronEventTypes,
    AmaronServiceBookingStatusEnum,
    AmaronServiceCategoryEnum,
    AMARON__EVENT__SERVICE__SERVICE_COMPLETED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__PACKAGES__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKING_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/Events";
import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {useNow} from "~/hooks/useNow";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions.ts";

/** The parent supplies its full event history. Deleted rows are already excluded by the Event API. */
function getCompletedServiceSummary(journey: EventContainerWithEvents, now: Date): {completedVisits: number; lastCompletedService: string | null; daysSinceLastService: number | null} {
    let completedVisits = 0;
    let lastCompletedService: string | null = null;
    let latestCompletionTime = Number.NEGATIVE_INFINITY;
    for (const event of journey.events) {
        const values = event.additionalColumnValues;
        if (event.eventContainerId !== journey.id || event.templateId !== AmaronEventTypes.Service as Uuid
            || event.status !== OttopilotEventStatus.active
            || values[AMARON__EVENT__SERVICE__BOOKING_STATUS__COLUMN_ID] !== AmaronServiceBookingStatusEnum.COMPLETED
            || values[AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID] === AmaronServiceCategoryEnum.CANCELLED
            || values[AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID] === AmaronServiceCategoryEnum.ARCHIVE) {
            continue;
        }
        completedVisits++;
        const completedAt = values[AMARON__EVENT__SERVICE__SERVICE_COMPLETED_AT__COLUMN_ID];
        const completionTime = typeof completedAt === "string" ? Date.parse(completedAt) : Number.NaN;
        if (Number.isFinite(completionTime) && completionTime > latestCompletionTime) {
            latestCompletionTime = completionTime;
            const packageName = values[AMARON__EVENT__SERVICE__PACKAGES__COLUMN_ID];
            lastCompletedService = typeof packageName === "string" && packageName.trim() !== "" ? packageName : null;
        }
    }
    // Count completions without timestamps, but never guess recency from booking/creation dates.
    const elapsed = now.getTime() - latestCompletionTime;
    const daysSinceLastService = Number.isFinite(elapsed) && elapsed >= 0 ? Math.floor(elapsed / 86_400_000) : null;
    return {completedVisits, lastCompletedService, daysSinceLastService};
}

/** Shared page/drawer presentation. Assistance belongs to the lead, not Opportunity Events. */
export function ProfileSalesAssistanceTabContent({journey}: {journey: EventContainerWithEvents}) {
    const value = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__SALES_ASSISTANCE_DATA__COLUMN_ID];
    const result = value === null || value === undefined ? null : salesAssistanceDataValidator.safeParse(value);
    const data = result?.success === true ? result.data : null;
    const now = useNow();
    const {completedVisits, lastCompletedService, daysSinceLastService} = useMemo(() => getCompletedServiceSummary(journey, now), [journey, now]);
    const serviceMetrics = [
        ...(lastCompletedService !== null ? [{label: "Last Completed Service", value: lastCompletedService}] : []),
        {label: "Completed Visits", value: completedVisits},
        ...(daysSinceLastService !== null ? [{label: "Days Since Last Service", value: daysSinceLastService}] : []),
    ];
    // Markdown is external producer content. Sanitize HTML before inserting it into the profile.
    const html = useMemo(() => data?.schemaId === SalesAssistanceSchema.markdown
        ? DOMPurify.sanitize(marked.parse(data.markdown, {async: false}), {USE_PROFILES: {html: true}})
        : "", [data]);
    if (result !== null && !result.success) {
        return <p role="alert" className="p-ds-space-200 text-ds-text-subtle">Sales assistance data could not be displayed.</p>;
    }
    if (data === null || (data.schemaId === SalesAssistanceSchema.markdown ? data.markdown.trim() === "" : data.schemaId === SalesAssistanceSchema.cards && data.cards.length === 0)) {
        return <p className="p-ds-space-200 text-ds-text-subtle">No sales assistance available right now for this lead.</p>;
    }
    return (
        <div className="@container grid h-full min-h-0 content-start gap-ds-space-200 overflow-y-auto p-ds-space-200">
            {data.schemaId === SalesAssistanceSchema.markdown ? (
                <div className="prose prose-sm max-w-none text-ds-text prose-headings:text-ds-text prose-a:text-ds-link" dangerouslySetInnerHTML={{__html: html}} />
            ) : data.schemaId === SalesAssistanceSchema.singleCard ? (
                <article aria-label={data.card.primaryRecommendedServicePackage} className="grid min-w-0 gap-ds-space-200 rounded border border-ds-border bg-ds-surface p-ds-space-200">
                    <header className="flex flex-wrap items-center justify-between gap-ds-space-100">
                        <div className="flex min-w-0 items-center gap-ds-space-100">
                            <h3 className="min-w-0 break-words text-lg font-semibold text-ds-text">{data.card.primaryRecommendedServicePackage}</h3>
                            {data.card.primaryRecommendationServicePackageDescription !== undefined && data.card.primaryRecommendationServicePackageDescription.trim() !== "" ? (
                                <AnankeTooltip content={data.card.primaryRecommendationServicePackageDescription}>
                                    <button type="button" aria-label="Primary service package description" className="inline-flex shrink-0 translate-y-0.5 items-center justify-center text-ds-icon-subtle">
                                        <InformationCircleIcon label="" color="var(--ds-icon-subtle)" />
                                    </button>
                                </AnankeTooltip>
                            ) : null}
                        </div>
                        <span className="text-sm text-ds-text-subtle">{amaronServiceRequestedTypes[data.card.primaryRecommendedServiceCategory].displayName}</span>
                    </header>
                    <div className="grid min-w-0 gap-ds-space-200 rounded border border-ds-border p-ds-space-100">
                        <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-ds-space-200 rounded-lg bg-ds-background-neutral p-ds-space-200 text-ds-text-subtle">
                            <span className="inline-flex h-6 translate-y-0.5 items-center justify-center">
                                <LightbulbIcon label="Insight" color="var(--ds-icon-accent-yellow)" />
                            </span>
                            <div className="min-w-0">
                                <p className="whitespace-pre-wrap break-words font-semibold leading-6">{data.card.primaryRecommendationPitchPoint}</p>
                                {data.card.primaryRecommendationFollowUpQuestion?.trim() ? (
                                    <p className="mt-ds-space-150 whitespace-pre-wrap break-words">Follow up: {data.card.primaryRecommendationFollowUpQuestion}</p>
                                ) : null}
                                <div className="mt-ds-space-150 flex flex-col gap-x-ds-space-200 gap-y-ds-space-100 border-t border-ds-border pt-ds-space-150 text-sm @min-[640px]:flex-row @min-[640px]:flex-wrap">
                                    {serviceMetrics.map((metric, index) => (
                                        <p key={metric.label} className="min-w-0 break-words">
                                            {index > 0 ? <span aria-hidden="true" className="mr-ds-space-200 hidden text-ds-text-subtlest @min-[640px]:inline">•</span> : null}
                                            {`${metric.label}: ${metric.value}`}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className={`grid min-w-0 items-start gap-ds-space-150 ${data.card.upSellRecommendationServicePackage?.trim() && data.card.crossSellRecommendedServicePackage?.trim() ? "@min-[640px]:grid-cols-2" : ""}`}>
                            {data.card.upSellRecommendationServicePackage?.trim() ? (
                                <details className="group min-w-0 rounded-lg bg-ds-background-accent-blue-subtlest p-ds-space-200">
                                    <summary className="flex cursor-pointer list-none items-start gap-ds-space-150 [&::-webkit-details-marker]:hidden">
                                        <span className="inline-flex h-6 shrink-0 translate-y-0.5 items-center justify-center text-ds-text-accent-blue"><ArrowUpIcon label="" /></span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block break-words font-semibold leading-6 text-ds-text">{data.card.upSellRecommendationServicePackage}</span>
                                            <span className="mt-ds-space-050 block text-sm text-ds-text-subtle">Service upgrade</span>
                                        </span>
                                        <span className="inline-flex h-6 shrink-0 translate-y-0.5 items-center justify-center text-ds-text-accent-blue group-not-open:rotate-180"><ChevronUpIcon label="" /></span>
                                    </summary>
                                    {data.card.upSellRecommendationRationale?.trim() ? (
                                        <>
                                            <p className="mt-ds-space-150 text-ds-text-accent-blue">Why recommend:</p>
                                            <p className="mt-ds-space-050 whitespace-pre-wrap break-words text-ds-text-subtle">{data.card.upSellRecommendationRationale}</p>
                                        </>
                                    ) : null}
                                </details>
                            ) : null}
                            {data.card.crossSellRecommendedServicePackage?.trim() ? (
                                <details className="group min-w-0 rounded-lg bg-ds-background-accent-purple-subtlest p-ds-space-200">
                                    <summary className="flex cursor-pointer list-none items-start gap-ds-space-150 [&::-webkit-details-marker]:hidden">
                                        <span className="inline-flex h-6 shrink-0 translate-y-0.5 items-center justify-center text-ds-text-accent-purple"><ToolsIcon label="" /></span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block break-words font-semibold leading-6 text-ds-text">{data.card.crossSellRecommendedServicePackage}</span>
                                            <span className="mt-ds-space-050 block text-sm text-ds-text-subtle">Additional service</span>
                                        </span>
                                        <span className="inline-flex h-6 shrink-0 translate-y-0.5 items-center justify-center text-ds-text-accent-purple group-not-open:rotate-180"><ChevronUpIcon label="" /></span>
                                    </summary>
                                    {data.card.crossSellRecommendationRationale?.trim() ? (
                                        <>
                                            <p className="mt-ds-space-150 text-ds-text-accent-purple">Why recommend:</p>
                                            <p className="mt-ds-space-050 whitespace-pre-wrap break-words text-ds-text-subtle">{data.card.crossSellRecommendationRationale}</p>
                                        </>
                                    ) : null}
                                </details>
                            ) : null}
                        </div>
                    </div>
                </article>
            ) : data.cards.map((card, index) => (
                <article key={index} aria-label={card.recommendedServicePackage} className="grid min-w-0 gap-ds-space-150 rounded border border-ds-border bg-ds-surface p-ds-space-150">
                    <div className="flex items-center justify-between gap-ds-space-150 border-b border-ds-border pb-ds-space-150">
                        <div className="flex min-w-0 items-center gap-ds-space-100">
                            <h3 className="min-w-0 break-words text-sm font-semibold leading-5 text-ds-text">{card.recommendedServicePackage}</h3>
                            {/* <span className="inline-flex shrink-0 text-ds-icon-subtle">
                                <InformationCircleIcon label="Service recommendation" color="var(--ds-icon-subtle)" />
                            </span> */}
                        </div>
                        <AnankeTempLozenge appearance="inprogress">
                            {amaronServiceRequestedTypes[card.recommendedServiceCategory].displayName}
                        </AnankeTempLozenge>
                    </div>
                    <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-ds-space-100 rounded-lg bg-ds-background-neutral p-ds-space-150 text-ds-text-subtle">
                        <span className="mt-ds-space-025 inline-flex shrink-0">
                            <LightbulbIcon label="Insight" color="var(--ds-icon-accent-yellow)" />
                        </span>
                        <div className="grid min-w-0 gap-ds-space-050">
                            <p className="whitespace-pre-wrap break-words text-sm font-semibold leading-5">{card.keyInsight}</p>
                            {card.content.trim() === "" ? null : (
                                <ul className="list-disc space-y-ds-space-050 pl-ds-space-250 text-sm leading-5">
                                    {card.content.split(/\r?\n/).filter(line => line.trim() !== "").map((line, lineIndex) => (
                                        <li key={lineIndex} className="break-words">{line}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </article>
            ))}
        </div>
    );
}
